<template>
 <div class="purchase-order-form">
  <form @submit.stop.prevent="form.handleSubmit()">
    <form.Field
      name="date"
      :validators="{
        onBlur: yup.string().required('Date is required')
      }"
    >
      <template #default="{ field }">
        <BaseInputDate
          v-model="(field.state.value as string)"
          :name="field.name"
          label="Payment date"
          :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
          @blur="field.handleBlur"
          @input="(e) => field.handleChange(e.target.value)"
        />
      </template>
    </form.Field>

    <form.Field
      name="method"
      :validators="{
        onBlur: yup.string().required('Payment method is required')
      }"
    >
      <template #default="{ field }">
        <BaseSelect
          v-model="(field.state.value as string)"
          :name="field.name"
          description="Select payment method"
          label="Method"
          :options="paymentMethods"
          :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
          @blur="field.handleBlur"
          @input="(e) => field.handleChange(e.target.value)"
        />
      </template>
    </form.Field>

    <form.Field
      name="amount"
      :validators="{
        onBlurListenTo: ['fullAmountChecked'],
        onBlur: yup.string().transform((value) => value.replace(/,/g, ''))
          .test('is-number','Amount must be a valid number and less than or equal to the bill balance: '+currency(bill.balance),(value) => testAmount(value))
      }"
    >
      <template #default="{ field }">
        <BaseInputCurrency
          v-model="(field.state.value as string)"
          :name="field.name"
          :disabled="fullAmount"
          :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
          @blur="field.handleBlur"
          @input="(e) => field.handleChange(e.target.value)"
        />
      </template>
    </form.Field>

    <form.Field
      name="fullAmountChecked"
    >
      <template #default="{ field }">
        <BaseCheckbox
          v-model="fullAmount"
          label="Full Amount"
          @change="(e) => {field.handleChange(e.target.checked); field.handleBlur()}"
        />
      </template>
    </form.Field>

    <form.Field
      name="memo"
      :validators="{
        onBlur: yup.string()
      }"
    >
      <template #default="{ field }">
        <Editor
          v-model="(field.state.value as string)"
          :additional-options="{ placeholder: 'Payment memo' }"
          @update:model-value="(value) => field.handleChange(value)"
        />
      </template>
    </form.Field>

    <form.Subscribe>
      <template #default="{ canSubmit, isSubmitting }">
        <div class="flex justify-end gap-2 mt-4">
          <BaseButton
            variant="base"
            type="button"
            text="Cancel"
            :disabled="isSubmitting || isPending"
            :class="{ 'cursor-not-allowed opacity-50': isSubmitting || isPending }"
            @click="emit('cancel')"
          />

          <BaseButton
            variant="primary"
            type="submit"
            text="Save"
            :disabled="!canSubmit || isSubmitting || isPending"
            :class="{ 'cursor-not-allowed opacity-50': !canSubmit || isSubmitting || isPending }"
          >
            <div class="flex justify-between items-center gap-2">
              <span>Save</span>
              <span
                v-if="isSubmitting || isPending"
                class="i-heroicons-arrow-path animate-spin"
              />
            </div>
          </BaseButton>
        </div>
      </template>
    </form.Subscribe>
  </form>
</div>
</template>

<script lang="ts" setup>
import { BillPayment, Bill } from '@/models/bill'
import BaseSelect from '@/components/base/BaseSelect.vue'
import BaseCheckbox from '@/components/base/BaseCheckbox.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import Editor from '@/components/TextEditor.vue'
import { ref, watch, onMounted } from 'vue'
import { useForm } from '@tanstack/vue-form'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import BaseInputCurrency from '@/components/base/BaseInputCurrency.vue'
import BaseInputDate from '@/components/base/BaseInputDate.vue'
import { currency, toNumber } from '@/utils'

const props = defineProps({
  bill: {
    type: Object as () => Bill,
    required: true
  },
  isPending: {
    type: Boolean,
    required: false,
    default: false
  }
})

const billPayment = defineModel({
  type: Object as () => BillPayment,
  default: () => new BillPayment()
})

const emit = defineEmits(['save', 'cancel'])

const fullAmount = ref(false)
const paymentMethods = [
  { label: 'Cash', value: 'cash' },
  { label: 'Check/ACH', value: 'check/ACH' },
  { label: 'Credit Card', value: 'creditCard' }
]

const form = useForm({
  defaultValues: {
    ...billPayment.value,
    fullAmountChecked: fullAmount.value,
  },
  validatorAdapter: yupValidator(),
  onSubmit: async ({ value }) => {
    const payment = {
      ...value,
      amount: toNumber(value.amount),
      date: new Date(value.date)
    }

    delete payment.fullAmountChecked

    emit('save', payment)
  }
})

watch(fullAmount, (newValue) => {
  if (newValue) {
    form.setFieldValue('amount', toNumber(props.bill.balance))
  } else {
    form.setFieldValue('amount', 0)
  }
})

onMounted(() => {
  form.reset()
})

const testAmount = (value: string | undefined) => {
  if (!value) return false
  const numValue = toNumber(value)

  if (props.bill.balance > 0) {
    return !isNaN(numValue) && numValue <= toNumber(props.bill.balance) && numValue > 0
  }
  return !isNaN(numValue) && numValue <= toNumber(props.bill.balance)
}

</script>
